import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  subDays,
} from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  X,
  Loader2,
} from 'lucide-react';
import { recordingsApi } from '../../services/api';

import { useTranslation } from 'react-i18next';
// ============================================
// TIMEZONE HELPERS
// ============================================

/**
 * Get the UTC offset in minutes for a given IANA timezone at a specific date.
 * Positive = ahead of UTC (e.g., +60 for CET), negative = behind.
 */
function getTimezoneOffsetMinutes(timezone: string, date: Date): number {
  try {
    const utcStr = date.toLocaleString('en-US', { timeZone: 'UTC' });
    const tzStr = date.toLocaleString('en-US', { timeZone: timezone });
    return (new Date(tzStr).getTime() - new Date(utcStr).getTime()) / 60000;
  } catch {
    return -date.getTimezoneOffset(); // fallback to browser timezone
  }
}

/**
 * Create a UTC Date from a local date/time string interpreted in a specific timezone.
 * E.g., "2026-03-16", 8, 57, 50, "Europe/Amsterdam" → UTC Date for 08:57:50 CET
 */
export function localTimeToUTC(date: Date, hours: number, minutes: number, seconds: number, timezone?: string): Date {
  if (!timezone) {
    // No timezone specified — use browser local time (legacy behavior)
    const result = new Date(date);
    result.setHours(hours, minutes, seconds, 0);
    return result;
  }
  // Build an ISO date string and interpret in the camera's timezone
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  // Treat as UTC first, then adjust by the camera timezone offset
  const asUTC = new Date(`${y}-${m}-${d}T${hh}:${mm}:${ss}Z`);
  const offsetMin = getTimezoneOffsetMinutes(timezone, asUTC);
  return new Date(asUTC.getTime() - offsetMin * 60000);
}

/**
 * Format a UTC Date as a time string in a specific timezone.
 */
export function formatTimeInTimezone(date: Date, timezone?: string): string {
  if (!timezone) return format(date, 'HH:mm:ss');
  try {
    return date.toLocaleTimeString('en-GB', { timeZone: timezone, hour12: false });
  } catch {
    return format(date, 'HH:mm:ss');
  }
}

// ============================================
// TYPES
// ============================================

export interface RecordingDatePickerProps {
  deviceId: string;
  isOpen: boolean;
  onClose: () => void;
  selectedDate: Date;
  selectedTime: Date;
  onDateTimeSelect: (dateTime: Date) => void;
  /** IANA timezone of the camera (e.g., 'Europe/Amsterdam'). If provided, time input is interpreted in this timezone. */
  timezone?: string;
}

// ============================================
// COMPONENT
// ============================================

export default function RecordingDatePicker({
  deviceId,
  isOpen,
  onClose,
  selectedDate,
  selectedTime,
  onDateTimeSelect,
  timezone,
}: RecordingDatePickerProps) {
  const { t } = useTranslation('cameras');
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(selectedDate));
  const [pickedDate, setPickedDate] = useState(selectedDate);
  // Initialize time fields in camera timezone if available
  const [hours, setHours] = useState(() => timezone ? formatTimeInTimezone(selectedTime, timezone).split(':')[0] : format(selectedTime, 'HH'));
  const [minutes, setMinutes] = useState(() => timezone ? formatTimeInTimezone(selectedTime, timezone).split(':')[1] : format(selectedTime, 'mm'));
  const [seconds, setSeconds] = useState(() => timezone ? formatTimeInTimezone(selectedTime, timezone).split(':')[2] : format(selectedTime, 'ss'));

  // Fetch days with recordings from API
  const [daysWithRecordings, setDaysWithRecordings] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !deviceId) return;

    const fetchCalendar = async () => {
      setLoading(true);
      try {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth() + 1; // API expects 1-12
        const data = await recordingsApi.getCalendar(deviceId, year, month);
        // API returns day-of-month numbers; map to yyyy-MM-dd keys to match the
        // calendar cell lookup below.
        const mm = String(month).padStart(2, '0');
        const keys = (data.daysWithRecordings || []).map(
          (d: number) => `${year}-${mm}-${String(d).padStart(2, '0')}`,
        );
        setDaysWithRecordings(new Set<string>(keys));
      } catch (err) {
        console.error('[RecordingDatePicker] Failed to fetch calendar:', err);
        setDaysWithRecordings(new Set());
      } finally {
        setLoading(false);
      }
    };

    fetchCalendar();
  }, [isOpen, deviceId, currentMonth]);

  // Generate calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 }); // Monday start
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const days: Date[] = [];
    let day = startDate;
    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  // Navigation
  const goToPreviousMonth = useCallback(() => {
    setCurrentMonth(subMonths(currentMonth, 1));
  }, [currentMonth]);

  const goToNextMonth = useCallback(() => {
    setCurrentMonth(addMonths(currentMonth, 1));
  }, [currentMonth]);

  // Quick navigation
  const goToToday = useCallback(() => {
    const now = new Date();
    setCurrentMonth(startOfMonth(now));
    setPickedDate(now);
    setHours(format(now, 'HH'));
    setMinutes(format(now, 'mm'));
    setSeconds(format(now, 'ss'));
  }, []);

  const goToYesterday = useCallback(() => {
    const yesterday = subDays(new Date(), 1);
    setCurrentMonth(startOfMonth(yesterday));
    setPickedDate(yesterday);
    setHours('12');
    setMinutes('00');
    setSeconds('00');
  }, []);

  const goToWeekAgo = useCallback(() => {
    const weekAgo = subDays(new Date(), 7);
    setCurrentMonth(startOfMonth(weekAgo));
    setPickedDate(weekAgo);
    setHours('12');
    setMinutes('00');
    setSeconds('00');
  }, []);

  // Handle date selection
  const handleDateClick = useCallback((date: Date) => {
    setPickedDate(date);
    if (!isSameMonth(date, currentMonth)) {
      setCurrentMonth(startOfMonth(date));
    }
  }, [currentMonth]);

  // Handle time input
  const handleHoursChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw === '') { setHours(''); return; }
    const num = parseInt(raw, 10);
    if (num > 23) { setHours('23'); return; }
    setHours(raw);
  }, []);

  const handleHoursBlur = useCallback(() => {
    setHours((prev) => (prev === '' ? '00' : prev.padStart(2, '0')));
  }, []);

  const handleMinutesChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw === '') { setMinutes(''); return; }
    const num = parseInt(raw, 10);
    if (num > 59) { setMinutes('59'); return; }
    setMinutes(raw);
  }, []);

  const handleMinutesBlur = useCallback(() => {
    setMinutes((prev) => (prev === '' ? '00' : prev.padStart(2, '0')));
  }, []);

  const handleSecondsChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 2);
    if (raw === '') { setSeconds(''); return; }
    const num = parseInt(raw, 10);
    if (num > 59) { setSeconds('59'); return; }
    setSeconds(raw);
  }, []);

  const handleSecondsBlur = useCallback(() => {
    setSeconds((prev) => (prev === '' ? '00' : prev.padStart(2, '0')));
  }, []);

  // Handle Go button
  const handleGo = useCallback(() => {
    const h = Math.min(23, Math.max(0, parseInt(hours || '0', 10)));
    const m = Math.min(59, Math.max(0, parseInt(minutes || '0', 10)));
    const s = Math.min(59, Math.max(0, parseInt(seconds || '0', 10)));
    // Interpret time in camera timezone (matches OSD display), fallback to browser local
    const dateTime = localTimeToUTC(pickedDate, h, m, s, timezone);
    onDateTimeSelect(dateTime);
    onClose();
  }, [pickedDate, hours, minutes, seconds, onDateTimeSelect, onClose, timezone]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-800 rounded-lg shadow-xl border border-gray-700 w-[380px]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-700">
          <div className="flex items-center gap-2 text-white">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">{t('goToDateTime')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Quick Navigation */}
        <div className="flex gap-2 px-4 py-2 border-b border-gray-700">
          <button
            onClick={goToToday}
            className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
          >
            {t('today')}
          </button>
          <button
            onClick={goToYesterday}
            className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
          >
            {t('yesterday')}
          </button>
          <button
            onClick={goToWeekAgo}
            className="px-3 py-1 text-sm bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
          >
            {t('s7Days')}
          </button>
        </div>

        {/* Calendar */}
        <div className="p-4">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-white font-medium">
              {format(currentMonth, 'MMMM yyyy')}
            </span>
            <button
              onClick={goToNextMonth}
              className="p-1 hover:bg-gray-700 rounded text-gray-400 hover:text-white transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          {/* Weekday Headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
              <div
                key={day}
                className="text-center text-xs text-gray-500 font-medium py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 relative">
            {loading && (
              <div className="absolute inset-0 bg-gray-800/50 flex items-center justify-center z-10 rounded">
                <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
              </div>
            )}
            {calendarDays.map((day, index) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const hasRecording = daysWithRecordings.has(dateKey);
              const isCurrentMonth = isSameMonth(day, currentMonth);
              const isSelected = isSameDay(day, pickedDate);
              const isTodayDate = isToday(day);

              return (
                <button
                  key={index}
                  onClick={() => handleDateClick(day)}
                  disabled={!isCurrentMonth}
                  className={`
                    relative p-2 text-sm rounded transition-colors
                    ${!isCurrentMonth ? 'text-gray-600 cursor-not-allowed' : ''}
                    ${isCurrentMonth && !isSelected ? 'text-gray-300 hover:bg-gray-700' : ''}
                    ${isSelected ? 'bg-blue-600 text-white' : ''}
                    ${isTodayDate && !isSelected ? 'ring-1 ring-blue-500' : ''}
                  `}
                >
                  {format(day, 'd')}
                  {/* Recording indicator dot */}
                  {hasRecording && isCurrentMonth && (
                    <span
                      className={`absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                        isSelected ? 'bg-white' : 'bg-green-500'
                      }`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Recording Legend */}
          <div className="flex items-center gap-2 mt-3 text-xs text-gray-400">
            <span className="w-2 h-2 rounded-full bg-green-500" />
            <span>{t('hasRecordings')}</span>
          </div>
        </div>

        {/* Time Input */}
        <div className="px-4 pb-4">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-400">{t('time')}{timezone ? ` (${timezone})` : ''}:</span>
            <div className="flex items-center gap-1">
              <input
                type="text"
                value={hours}
                onChange={handleHoursChange}
                onBlur={handleHoursBlur}
                className="w-12 px-2 py-1 text-center text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="HH"
                maxLength={2}
              />
              <span className="text-gray-400">:</span>
              <input
                type="text"
                value={minutes}
                onChange={handleMinutesChange}
                onBlur={handleMinutesBlur}
                className="w-12 px-2 py-1 text-center text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="MM"
                maxLength={2}
              />
              <span className="text-gray-400">:</span>
              <input
                type="text"
                value={seconds}
                onChange={handleSecondsChange}
                onBlur={handleSecondsBlur}
                className="w-12 px-2 py-1 text-center text-white bg-gray-700 border border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="SS"
                maxLength={2}
              />
            </div>
          </div>

          {/* Selected Date/Time Preview */}
          <div className="mb-4 p-2 bg-gray-700/50 rounded text-center">
            <span className="text-sm text-gray-400">{t('selected')} </span>
            <span className="text-white font-mono">
              {format(pickedDate, 'yyyy-MM-dd')} {hours}:{minutes}:{seconds}
            </span>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-sm bg-gray-700 hover:bg-gray-600 rounded text-white transition-colors"
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleGo}
              className="flex-1 px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 rounded text-white font-medium transition-colors"
            >
              {t('goToTime')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
